import type { CertificateDocumentDto } from '../../api/hr-certificate';

/**
 * 증명서 한 장 — 화면과 인쇄가 **같은 마크업**을 쓴다.
 *
 * ★★ 이 조각은 저장된 파일을 그리는 것이 아니라, 서버가 **발급 시점의 인사 정보로 다시 만들어 준
 *    값**을 그린다(migration 129 머리말). 그래서 「미리보기」와 「출력」이 서로 다를 수 없다.
 * ★ 인쇄는 브라우저에 맡긴다 — PDF 라이브러리를 넣지 않는다. `@media print` 가 이 조각만 남긴다.
 * ★ 주민번호는 우리 표에 **없다.** `maskRrn` 은 «뒷자리를 가려 달라» 는 요청이고, 지금 이 종이에는
 *   생년월일 칸 자체가 없다 — 인사기록에 그 값이 들어오는 날 이 자리에 붙인다.
 */
/**
 * 회사 직인 그림 — **지금** 회사 직인이다.
 *
 * ★★★ 옛 서버가 `sealSrc` 를 안 줄 때만 쓰는 되돌아갈 자리다. 이것만 쓰면 회사가 직인을
 *    새로 등록했을 때 **이미 나간 증명서에도 도장이 찍힌다**(2026-09-01 에 실제로 겪었다).
 *    새 서버는 그 문서에 찍힌 그림의 주소(`sealSrc`)를 준다.
 */
const SEAL_URL = `${(import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api/v1'}/institution/seal`;

export function CertificateDoc({ doc }: { doc: CertificateDocumentDto }) {
  const issued = doc.issuedAt ? doc.issuedAt.slice(0, 10) : null;
  const issuedKo = issued
    ? `${issued.slice(0, 4)}년 ${Number(issued.slice(5, 7))}월 ${Number(issued.slice(8, 10))}일`
    : '';

  return (
    <article className="hrdoc" aria-label={doc.kindLabel}>
      <h3 className="hrdoc__title">{[...doc.kindLabel].join(' ')}</h3>

      <dl className="hrdoc__fields">
        <div><dt>성 명</dt><dd>{doc.name}</dd></div>
        {doc.employeeNumber ? <div><dt>사 번</dt><dd>{doc.employeeNumber}</dd></div> : null}
        <div><dt>소 속</dt><dd>{doc.belong}</dd></div>
        {/*
          ★★ 사업장 주소 (2026-08-30 · 판 A1) — 증명서에 주소가 **아예 없었다**.
             재직증명서는 제출처가 「어느 사업장에서 일하는가」를 보는 서류다.
          ★ 근무지가 있으면 그 이름을 앞에 적는다(「부산지사 · 부산시 …」) — 회사 주소로
            떨어진 경우에는 이름을 적지 않는다(「본사」라고 쓰면 없는 사업장을 지어낸다).
          ★ 주소가 없으면 **줄을 안 그린다** — 빈 줄은 인쇄물에서 지운 것처럼 보인다.
        */}
        {doc.address ? (
          <div>
            <dt>근 무 지</dt>
            <dd>{doc.worksiteName ? `${doc.worksiteName} · ${doc.address}` : doc.address}</dd>
          </div>
        ) : null}
        <div><dt>직 위</dt><dd>{doc.position}</dd></div>
        <div><dt>입 사 일</dt><dd>{doc.hireDate ?? '—'}</dd></div>
        <div><dt>재직기간</dt><dd>{doc.period}{doc.tenure ? ` (${doc.tenure})` : ''}</dd></div>
        {doc.purpose || doc.recipient ? (
          <div>
            <dt>용 도</dt>
            <dd>{[doc.purpose, doc.recipient].filter(Boolean).join(' · ')}</dd>
          </div>
        ) : null}
      </dl>

      <p className="hrdoc__statement">{doc.statement}</p>
      <p className="hrdoc__date">{issuedKo}</p>
      <p className="hrdoc__company">
        {doc.companyName}
        {doc.representativeName ? <>　대표 {doc.representativeName}</> : null}
        {/*
          ★ 직인 (2026-08-28 · 판 8) — 자리는 원래 있었고 **넣을 그림이 없었을 뿐**이다.
            등록 안 했으면 지금처럼 글자로 남는다(발급을 막지 않는다).
        */}
        {/*
          ★★ **그때 찍힌 도장을 그린다.** `sealSrc` 는 그 문서에 굳은 그림이고, 없으면
             «직인 없이 나간 종이» 다 — 지금 회사에 직인이 있어도 찍지 않는다.
          ★ `sealSrc` 자체가 없는 응답(옛 서버)일 때만 종전대로 `hasSeal` 로 떨어진다.
        */}
        {doc.sealSrc
          ? <img className="hrdoc__sealimg" src={doc.sealSrc} alt="회사 직인" />
          : doc.sealSrc === undefined && doc.hasSeal
            ? <img className="hrdoc__sealimg" src={SEAL_URL} alt="회사 직인" />
            : <span className="hrdoc__seal">직인</span>}
      </p>
      {doc.docNo ? <p className="hrdoc__no">발급번호 {doc.docNo}{doc.copies > 1 ? ` · ${doc.copies}부` : ''}</p> : null}
    </article>
  );
}
